import { z } from 'zod/v4'

import { defineScreen, type ScreenData, type ScreenQuery } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

import { adminPaginationSchema } from './users.screen.js'

/*
 * «Soporte»: the list (one page of tickets with the filters and the order of the old route, the counts by status and
 * the assignable administrators) and the detail (one ticket with its messages and the administrators). Tickets carry
 * personal data of customers and each row carries ONLY what its page paints: the list has neither the e-mail nor the
 * identifiers of the customer, the detail has neither his name nor any identifier of him (of the staff only
 * `assignedToId` and the `admins` of the selector travel; a message says whether it is the caller's, the server
 * compares), and a message has not the e-mail of its author.
 */

export const SUPPORT_TICKET_STATUSES = [
  'open',
  'in_progress',
  'waiting_customer',
  'resolved',
  'closed',
] as const
export const SUPPORT_TICKET_PRIORITIES = ['low', 'medium', 'high', 'critical'] as const
export const SUPPORT_TICKET_CATEGORIES = [
  'billing',
  'technical',
  'account',
  'feature_request',
  'bug',
  'other',
] as const
/** The ones of `listTicketsQuerySchema`: the page and the journey keep the names. */
export const SUPPORT_TICKET_SORT_FIELDS = [
  'subject',
  'status',
  'createdAt',
  'updatedAt',
  'priority',
] as const

/** What the row of the list and the detail of a ticket both paint. */
const supportTicketCoreShape = {
  id: z.string(),
  subject: z.string(),
  status: z.enum(SUPPORT_TICKET_STATUSES),
  priority: z.enum(SUPPORT_TICKET_PRIORITIES),
  /** `null` in the tickets created before the category existed. */
  category: z.enum(SUPPORT_TICKET_CATEGORIES).nullable(),
  tenantName: z.string(),
  assignedToId: z.string().nullable(),
  assignedToName: z.string().nullable(),
  createdAt: z.string(),
}

export const supportTicketRowSchema = z.strictObject({
  ...supportTicketCoreShape,
  /** Name and surnames of the customer, as the table paints them; his e-mail does NOT travel in the list. */
  userName: z.string(),
})

export const supportTicketStatsSchema = z.strictObject({
  total: z.number(),
  open: z.number(),
  inProgress: z.number(),
  waitingCustomer: z.number(),
  resolved: z.number(),
  closed: z.number(),
})

/** The assignment selector paints name and e-mail. */
export const supportAdminOptionSchema = z.strictObject({
  id: z.string(),
  name: z.string(),
  email: z.string(),
})

export const supportTicketMessageSchema = z.strictObject({
  id: z.string(),
  /** Whether the author is the one who asks (the server compares); the id and the e-mail of the author do NOT travel. */
  isFromCaller: z.boolean(),
  authorName: z.string(),
  content: z.string(),
  isInternal: z.boolean(),
  createdAt: z.string(),
})

export const supportTicketDetailSchema = z.strictObject({
  ...supportTicketCoreShape,
  /** The detail paints the e-mail of the customer, not his name. */
  userEmail: z.string(),
  updatedAt: z.string(),
  closedAt: z.string().nullable(),
  messageCount: z.number(),
  lastMessageAt: z.string().nullable(),
  messages: z.array(supportTicketMessageSchema),
})

/** The names of the old route, so the page and the journey change only the route. No `tenantId`: the page has no such filter. */
export const adminSupportQuerySchema = z.strictObject({
  page: z.coerce.number<number>().int().min(1).default(1),
  limit: z.coerce.number<number>().int().min(1).max(100).default(20),
  status: z.enum(SUPPORT_TICKET_STATUSES).optional(),
  priority: z.enum(SUPPORT_TICKET_PRIORITIES).optional(),
  category: z.enum(SUPPORT_TICKET_CATEGORIES).optional(),
  assignedToId: z.union([z.guid(), z.literal('current'), z.literal('none')]).optional(),
  search: z.string().min(1).max(200).optional(),
  sortBy: z.enum(SUPPORT_TICKET_SORT_FIELDS).default('createdAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
})

export const adminSupportScreen = defineScreen({
  id: 'admin.support',
  path: '/api/screens/admin/support',
  access: 'super_admin',
  query: adminSupportQuerySchema,
  response: z.strictObject({
    page: screenSection(
      z.strictObject({ items: z.array(supportTicketRowSchema), pagination: adminPaginationSchema }),
    ),
    stats: screenSection(supportTicketStatsSchema),
    admins: screenSection(z.array(supportAdminOptionSchema)),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.support'],
  budget: { requests: 1, statements: 12, bytes: 120_000, p95Ms: 400 },
  summary:
    'Support: one page of tickets with the filters and order of the list, the counts by status and the assignable administrators',
})

export const adminSupportDetailQuerySchema = z.strictObject({ id: z.guid() })

export const adminSupportDetailScreen = defineScreen({
  id: 'admin.support.detail',
  path: '/api/screens/admin/support/detail',
  access: 'super_admin',
  query: adminSupportDetailQuerySchema,
  response: z.strictObject({
    ticket: supportTicketDetailSchema,
    admins: screenSection(z.array(supportAdminOptionSchema)),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.support'],
  budget: { requests: 1, statements: 8, bytes: 250_000, p95Ms: 400 },
  summary: 'Support › one ticket: the ticket with its messages and the assignable administrators',
})

export type SupportTicketRow = z.infer<typeof supportTicketRowSchema>
export type SupportTicketStats = z.infer<typeof supportTicketStatsSchema>
export type SupportAdminOption = z.infer<typeof supportAdminOptionSchema>
export type SupportTicketMessage = z.infer<typeof supportTicketMessageSchema>
export type SupportTicketDetail = z.infer<typeof supportTicketDetailSchema>
export type SupportTicketSortField = (typeof SUPPORT_TICKET_SORT_FIELDS)[number]
export type AdminSupportQuery = ScreenQuery<typeof adminSupportScreen>
export type AdminSupportData = ScreenData<typeof adminSupportScreen>
export type AdminSupportDetailQuery = ScreenQuery<typeof adminSupportDetailScreen>
export type AdminSupportDetailData = ScreenData<typeof adminSupportDetailScreen>
